import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Select, 
  Popover, 
  Modal,
  message, 
  Space, 
  Typography, 
  Row,
  Col,
  Input,
  InputNumber,
  Form,
  Statistic,
  Alert,
  Tooltip,
  Checkbox
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  PercentageOutlined,
  NumberOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { salesTaxRulesService } from '../../services/salesTaxRulesService';
import './SalesTaxRules.css';

const { Title, Text } = Typography;
const { Option } = Select;

// US States options
const US_STATES_OPTIONS = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' }, 
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

function SalesTaxRules() {
  const [loading, setLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState({ federalRate: null, jurisdictions: [] });
  const [searchValue, setSearchValue] = useState('');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('zipcode');
  const [showPercentage, setShowPercentage] = useState(true);
  
  // Edit state
  const [editingCell, setEditingCell] = useState(null);
  const [editForm] = Form.useForm();
  const [bulkEditVisible, setBulkEditVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    fetchHierarchyData();
  }, [selectedState, selectedCounty, selectedCity]);

  useEffect(() => {
    if (selectedState) {
      loadCounties(selectedState);
    } else {
      setAvailableCounties([]);
      setSelectedCounty(null);
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedState && selectedCounty) {
      loadCities(selectedState, selectedCounty);
    } else {
      setAvailableCities([]);
      setSelectedCity(null);
    }
  }, [selectedState, selectedCounty]);

  const fetchHierarchyData = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedState) filters.state = selectedState;
      if (selectedCounty) filters.county = selectedCounty;
      if (selectedCity) filters.city = selectedCity;

      const data = await salesTaxRulesService.getHierarchicalJurisdictions(filters);
      
      // Apply search filter if needed
      let filteredData = data;
      if (searchValue && data.jurisdictions) {
        filteredData = {
          ...data,
          jurisdictions: applySearchFilter(data.jurisdictions, searchValue)
        };
      }
      
      setHierarchyData(filteredData || { federalRate: null, jurisdictions: [] });
    } catch (error) {
      console.error('Error fetching hierarchy data:', error);
      messageApi.error('Failed to fetch sales tax data');
    } finally {
      setLoading(false);
    }
  };

  const loadCounties = async (state) => {
    try {
      const counties = await salesTaxRulesService.getAvailableCounties(state);
      setAvailableCounties(counties);
    } catch (error) {
      console.error('Error loading counties:', error);
    }
  };

  const loadCities = async (state, county) => {
    try {
      const cities = await salesTaxRulesService.getAvailableCities(state, county);
      setAvailableCities(cities);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const applySearchFilter = (data, searchTerm) => {
    const term = searchTerm.toLowerCase();
    return data.filter(county => {
      // Search in county name
      if (county.name.toLowerCase().includes(term)) return true;
      
      // Search in cities
      return county.children.some(city => {
        if (city.name.toLowerCase().includes(term)) return true;
        
        // Search in ZIP codes
        return city.children.some(zip => 
          zip.zipCode.toLowerCase().includes(term)
        );
      });
    });
  };

  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return 'N/A';
    // Rate is stored as decimal (0.09375 for 9.375%)
    return showPercentage ? `${(rate * 100).toFixed(3)}%` : rate.toFixed(6);
  };

  const handleRowExpand = (expanded, record) => {
    const key = record.id;
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, key]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(k => k !== key));
    }
  };

  const handleFederalEdit = async (taxRate) => {
    try {
      await salesTaxRulesService.updateJurisdictionTaxRate({}, 'federal', taxRate);
      messageApi.success('Federal tax rate updated successfully');
      fetchHierarchyData();
    } catch (error) {
      console.error('Federal edit error:', error);
      messageApi.error('Failed to update federal tax rate');
    }
  };

  const handleJurisdictionEdit = async (record, taxRate, applyToSameCity = false) => {
    try {
      const jurisdiction = {
        state: record.state,
        county: record.county,
        city: record.city,
        zipCode: record.zipCode
      };

      if (applyToSameCity && record.type === 'zipcode') {
        await salesTaxRulesService.applyRateToSameCityZipCodes(jurisdiction, taxRate);
        messageApi.success(`Applied rate to all ZIP codes in ${record.city}`);
      } else {
        await salesTaxRulesService.updateJurisdictionTaxRate(jurisdiction, record.type, taxRate);
        messageApi.success(`${record.type} tax rate updated successfully`);
      }
      
      fetchHierarchyData();
      setEditingCell(null);
    } catch (error) {
      console.error('Edit error:', error);
      messageApi.error('Failed to update tax rate');
    }
  };

  const handleBulkEdit = () => {
    if (selectedRowKeys.length === 0) {
      messageApi.warning(`Please select ${selectedLevel} records to edit`);
      return;
    }
    // Show bulk edit interface
    setBulkEditVisible(true);
  };

  const handleBulkEditSubmit = async (taxRate) => {
    try {
      const selectedRecords = getSelectedRecords();
      const jurisdictions = selectedRecords.map(record => ({
        state: record.state,
        county: record.county,
        city: record.city,
        zipCode: record.zipCode
      }));

      const result = await salesTaxRulesService.bulkUpdateLevelRates(jurisdictions, selectedLevel, taxRate);
      
      if (result.updated.length > 0) {
        messageApi.success(`Successfully updated ${result.updated.length} ${selectedLevel} tax rates`);
        setSelectedRowKeys([]);
        fetchHierarchyData();
      }

      if (result.errors.length > 0) {
        messageApi.error(`${result.errors.length} updates failed`);
      }
    } catch (error) {
      console.error('Bulk edit error:', error);
      messageApi.error('Failed to update tax rates');
    }
  };

  // Helper method to get selected records
  const getSelectedRecords = () => {
    const allRecords = [];
    const addRecords = (items) => {
      items.forEach(item => {
        allRecords.push(item);
        if (item.children) {
          addRecords(item.children);
        }
      });
    };
    addRecords(buildTableData());
    return allRecords.filter(record => selectedRowKeys.includes(record.key));
  };

  // Build expandable table structure
  const buildTableData = () => {
    if (!hierarchyData.jurisdictions) return [];
    
    return hierarchyData.jurisdictions.map(state => ({
      key: state.id,
      id: state.id,
      type: 'state',
      name: state.name,
      state: state.state,
      taxRate: state.taxRate,
      totalZipCodes: state.totalZipCodes,
      children: state.children.map(county => ({
        key: county.id,
        id: county.id,
        type: 'county',
        name: county.name,
        state: county.state,
        county: county.county,
        taxRate: county.taxRate,
        totalZipCodes: county.totalZipCodes,
        children: county.children.map(city => ({
          key: city.id,
          id: city.id,
          type: 'city',
          name: city.name,
          state: city.state,
          county: city.county,
          city: city.city,
          taxRate: city.taxRate,
          totalZipCodes: city.totalZipCodes,
          children: city.children.map(zip => ({
            key: zip.id,
            id: zip.id,
            type: 'zipcode',
            zipCode: zip.zipCode,
            state: zip.state,
            county: zip.county,
            city: zip.city,
            taxRate: zip.taxRate,
            isActive: zip.isActive
          }))
        }))
      }))
    }));
  };

  // Create edit popover content
  const createEditPopover = (record) => {
    const isZipCode = record.type === 'zipcode';
    
    return (
      <div style={{ width: 200 }}>
        <Form
          form={editForm}
          layout="vertical"
          size="small"
          onFinish={(values) => {
            // Convert percentage input to decimal for storage
            handleJurisdictionEdit(record, values.taxRate / 100, values.applyToSameCity);
          }}
          initialValues={{ taxRate: (record.taxRate || 0) * 100 }}
        >
          <Form.Item
            name="taxRate"
            label={`${record.type.charAt(0).toUpperCase() + record.type.slice(1)} Tax Rate (%)`}
            rules={[{ required: true, message: 'Please enter tax rate' }]}
          >
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              style={{ width: '100%' }}
              placeholder="0.00"
            />
          </Form.Item>
          
          {isZipCode && (
            <Form.Item name="applyToSameCity" valuePropName="checked">
              <Checkbox>Apply to all ZIP codes in {record.city}</Checkbox>
            </Form.Item>
          )}
          
          <div style={{ textAlign: 'right' }}>
            <Button 
              size="small" 
              onClick={() => setEditingCell(null)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              size="small" 
              htmlType="submit"
            >
              Update
            </Button>
          </div>
        </Form>
      </div>
    );
  };

  const columns = [
    {
      title: 'Location',
      dataIndex: 'name',
      key: 'location',
      width: 300,
      render: (text, record) => {
        const indent = record.type === 'city' ? 20 : record.type === 'county' ? 10 : record.type === 'zipcode' ? 30 : 0;
        
        return (
          <div style={{ paddingLeft: indent, display: 'flex', alignItems: 'center' }}>
            <div>
              <Text strong={record.type !== 'zipcode'}>
                {record.type === 'zipcode' ? `ZIP ${record.zipCode}` : text}
              </Text>
              {record.type !== 'zipcode' && record.totalZipCodes > 0 && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.totalZipCodes} ZIP code{record.totalZipCodes !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Tax Rate',
      dataIndex: 'taxRate',
      key: 'rate',
      width: 150,
      render: (rate, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong>{formatRate(rate)}</Text>
            {record.type !== 'state' && record.type !== 'county' && record.type !== 'city' && record.type !== 'zipcode' ? null : (
              <Popover
                content={createEditPopover(record)}
                title={`Edit ${record.type} Tax Rate`}
                trigger="click"
                open={editingCell === record.key}
                onOpenChange={(visible) => {
                  if (visible) {
                    setEditingCell(record.key);
                    editForm.setFieldsValue({ taxRate: record.taxRate || 0 });
                  } else {
                    setEditingCell(null);
                  }
                }}
              >
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit
                </Button>
              </Popover>
            )}
          </div>
        );
      }
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 80,
      render: (state) => <Text>{state}</Text>
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      // Only allow selection of rows matching the selected level
      const validKeys = newSelectedRowKeys.filter(key => {
        const record = findRecordByKey(buildTableData(), key);
        return record && record.type === selectedLevel;
      });
      setSelectedRowKeys(validKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.type !== selectedLevel,
      name: record.name,
    }),
  };

  const findRecordByKey = (data, key) => {
    for (const item of data) {
      if (item.key === key) return item;
      if (item.children) {
        const found = findRecordByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const tableData = buildTableData();

  return (
    <div className="sales-tax-rules">
      {contextHolder}
      <div className="sales-tax-rules-header">
        <Title level={2}>Sales Tax Management</Title>
        <Text type="secondary">
          Manage sales tax rates by jurisdiction level with hierarchical organization
        </Text>
      </div>

      {/* Federal Tax Rate */}
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle">
          <Col span={4}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <GlobalOutlined style={{ color: '#D4AF37' }} />
              <Text strong>Federal Tax Rate</Text>
            </div>
          </Col>
          <Col span={4}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>{formatRate(hierarchyData.federalRate)}</Text>
              <Popover
                content={
                  <div style={{ width: 200 }}>
                    <Form
                      layout="vertical"
                      size="small"
                      onFinish={(values) => handleFederalEdit(values.taxRate / 100)}
                      initialValues={{ taxRate: (hierarchyData.federalRate || 0) * 100 }}
                    >
                      <Form.Item
                        name="taxRate"
                        label="Federal Tax Rate (%)"
                        rules={[{ required: true, message: 'Please enter tax rate' }]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          step={0.01}
                          style={{ width: '100%' }}
                          placeholder="0.00"
                        />
                      </Form.Item>
                      
                      <div style={{ textAlign: 'right' }}>
                        <Button type="primary" size="small" htmlType="submit">
                          Update
                        </Button>
                      </div>
                    </Form>
                  </div>
                }
                title="Edit Federal Tax Rate"
                trigger="click"
              >
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit
                </Button>
              </Popover>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Cascading Filters */}
      <Card className="filters-card" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={3}>
            <Select
              placeholder="Select State"
              value={selectedState}
              onChange={setSelectedState}
              allowClear
              style={{ width: '100%' }}
            >
              {US_STATES_OPTIONS.map(state => (
                <Option key={state.code} value={state.code}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="Select County"
              value={selectedCounty}
              onChange={setSelectedCounty}
              allowClear
              disabled={!selectedState}
              style={{ width: '100%' }}
            >
              {availableCounties.map(county => (
                <Option key={county} value={county}>
                  {county}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="Select City"
              value={selectedCity}
              onChange={setSelectedCity}
              allowClear
              disabled={!selectedCounty}
              style={{ width: '100%' }}
            >
              {availableCities.map(city => (
                <Option key={city} value={city}>
                  {city}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Input
              placeholder="Search ZIP, city, county..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={fetchHierarchyData}
            />
          </Col>
          <Col span={3}>
            <Select
              placeholder="Bulk Edit Level"
              value={selectedLevel}
              onChange={(value) => {
                setSelectedLevel(value);
                setSelectedRowKeys([]); // Clear selection when level changes
              }}
              style={{ width: '100%' }}
            >
              <Option value="state">State</Option>
              <Option value="county">County</Option>
              <Option value="city">City</Option>
              <Option value="zipcode">ZIP Code</Option>
            </Select>
          </Col>
          <Col span={9}>
            <Space>
              <Button
                type="primary"
                onClick={handleBulkEdit}
                disabled={selectedRowKeys.length === 0}
              >
                Bulk Edit {selectedLevel} ({selectedRowKeys.length})
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchHierarchyData}
                loading={loading}
              >
                Refresh
              </Button>
              <Tooltip title="Toggle between percentage and decimal display">
                <Button
                  icon={showPercentage ? <PercentageOutlined /> : <NumberOutlined />}
                  onClick={() => setShowPercentage(!showPercentage)}
                >
                  {showPercentage ? '%' : 'Dec'}
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      {selectedState && hierarchyData.jurisdictions && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="Total States" 
                value={hierarchyData.jurisdictions.length}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Total Counties" 
                value={hierarchyData.jurisdictions.reduce((sum, state) => sum + (state.children?.length || 0), 0)}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Total ZIP Codes" 
                value={hierarchyData.jurisdictions.reduce((sum, state) => sum + (state.totalZipCodes || 0), 0)}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={`Selected ${selectedLevel}`}
                value={selectedRowKeys.length}
                suffix="for bulk edit"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Main Table */}
      <Card className="rules-table-card">
        {!selectedState ? (
          <Alert
            message="Select a state to view sales tax data"
            description="Use the state filter above to browse sales tax rates by state, county, city, and ZIP code."
            type="info"
            showIcon
            style={{ margin: '20px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            rowSelection={rowSelection}
            expandable={{
              expandedRowKeys,
              onExpand: handleRowExpand,
              expandRowByClick: true,
              rowExpandable: (record) => record.type !== 'zipcode',
            }}
            rowClassName={(record) => {
              switch (record.type) {
                case 'state': return 'hierarchy-row-state';
                case 'county': return 'hierarchy-row-county';
                case 'city': return 'hierarchy-row-city';
                case 'zipcode': return 'hierarchy-row-zipcode';
                default: return '';
              }
            }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} items`
            }}
            size="middle"
            scroll={{ x: true }}
          />
        )}
      </Card>

      {/* Simple Bulk Edit Modal */}
      {bulkEditVisible && (
        <Modal
          title={`Bulk Edit ${selectedLevel} Tax Rates (${selectedRowKeys.length} selected)`}
          open={bulkEditVisible}
          onCancel={() => setBulkEditVisible(false)}
          onOk={() => {
            const form = document.getElementById('bulk-edit-form');
            if (form) {
              const formData = new FormData(form);
              const taxRate = parseFloat(formData.get('taxRate'));
              if (!isNaN(taxRate)) {
                // Convert percentage input to decimal for storage
                handleBulkEditSubmit(taxRate / 100);
                setBulkEditVisible(false);
              }
            }
          }}
          width={400}
        >
          <Alert
            message={`Bulk ${selectedLevel} Rate Update`}
            description={`Set the same tax rate for all selected ${selectedLevel} records.`}
            type="info"
            style={{ marginBottom: 16 }}
          />
          
          <form id="bulk-edit-form">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                {selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)} Tax Rate (%)
              </label>
              <InputNumber
                name="taxRate"
                min={0}
                max={100}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default SalesTaxRules;